/* eslint-disable no-empty -- theme house style: storage, history and focus calls fail silently by design. */
/*
 * Cadoo — account Saved Gifts and Secret Santa, behind the one storefront heart.
 *
 * Loaded by layout/theme.liquid directly after assets/cadoo.js. It plugs into
 * the heart engine's provider hook (window.CadooWishlist.use) BEFORE the
 * page's first paint, so there is exactly one painter and no flash of
 * another state.
 *
 * WHO SEES WHAT
 *   Signed out      This file installs nothing. The heart is the existing
 *                   device-local list in localStorage ("cadoo_wishlist_v1"),
 *                   exactly as before. No server call, no group write, even
 *                   if a stale Secret Santa context is in the tab.
 *
 *   Signed in       The heart is the customer's ACCOUNT Saved Gifts, read
 *                   from the server once per page (one batched request) and
 *                   kept in memory. Add and remove write the account list
 *                   only. A server failure keeps the last confirmed state and
 *                   says "Couldn't save. Try again." - it never falls back to
 *                   the anonymous list.
 *
 *   Signed in, in   The tab arrived from a group's "Browse gifts" link, whose
 *   Secret Santa    short-lived signed context the layout moved from the URL
 *   mode            fragment into this tab's sessionStorage before analytics
 *                   ran. On product cards and product pages the heart now
 *                   means "on THIS group's Secret Santa wishlist":
 *                     - selecting adds the exact option to the group AND makes
 *                       sure it is in account Saved Gifts (one server call,
 *                       one database transaction);
 *                     - unselecting removes it from the group ONLY; it stays
 *                       in Saved Gifts, and the message says so;
 *                     - a product saved to the account but not to this group
 *                       reads as unselected;
 *                     - a multi-option card saves to the account and opens the
 *                       product page in this tab to choose; the option visibly
 *                       selected there is the one that goes to the group, and
 *                       a different option replaces the group's saved one.
 *                   The Saved Gifts page and drawer always show and change the
 *                   ACCOUNT list, whatever mode the tab is in.
 *
 * WHAT NEVER HAPPENS HERE
 *   The context token never goes into a query string, a cookie, localStorage
 *   or a request URL: only the body of a POST to the signed app proxy. No
 *   customer id - raw or hashed - exists in this file; the server resolves
 *   identity from Shopify's signed session. Authenticated state (account
 *   handles, group name, group items) lives in memory for the current page
 *   ONLY: it is never written to sessionStorage, localStorage, a cookie or a
 *   URL, so a second customer signing in within the same tab can never
 *   inherit the previous customer's hearts. Every page starts neutral and
 *   shows saved state only after the signed list response for the current
 *   customer arrives. The one thing kept in this tab's sessionStorage is the
 *   signed context token itself, which the server re-validates on every use.
 *   An anonymous device list is never merged silently: the customer is asked
 *   once, with a count, and the import goes to the account only.
 */
(function () {
  'use strict';

  var CFG = window.CadooConfig || {};
  var SIGNED_IN = CFG.signedIn === true;
  var ENDPOINT = (CFG.secretSanta && CFG.secretSanta.endpoint) || '/apps/secret-santa/storefront-wishlist';
  var PARAM = 'cadoo_ss';
  var TOKEN_SHAPE = /^ssc1\.[A-Za-z0-9_-]{16,900}\.[A-Za-z0-9_-]{43}$/;

  /* Tab-scoped (sessionStorage) */
  var TOKEN_KEY = 'cadoo_ss_token_v1';
  var TOKEN_FRESH_KEY = 'cadoo_ss_token_fresh_v1';
  /* Written by an earlier build of this module; deleted at startup, never read. */
  var LEGACY_CACHE_KEY = 'cadoo_ss_cache_v1';
  var PROMPT_KEY = 'cadoo_ss_prompt_v1';
  /* Device-scoped (localStorage), non-identifying */
  var IMPORT_FLAG_KEY = 'cadoo_wishlist_import_v1';
  /* The theme's own anonymous list, read only for the import prompt. */
  var ANON_KEY = 'cadoo_wishlist_v1';

  var REVALIDATE_AFTER_MS = 30 * 1000;
  var IMPORT_CHUNK = 100;

  var TEXT = {
    region: 'Secret Santa shopping',
    indicator: 'Hearts save to the Secret Santa wishlist for ',
    exit: 'Exit',
    exitLabel: 'Exit Secret Santa shopping and go back to Saved Gifts',
    saved: 'Saved to your Saved Gifts.',
    unsaved: 'Removed from Saved Gifts.',
    groupAdded: 'Added to your Secret Santa wishlist and Saved Gifts.',
    groupReplaced: 'Updated the option on your Secret Santa wishlist.',
    groupRemoved: 'Removed from your Secret Santa wishlist. Still in Saved Gifts.',
    chooseOption: 'Choose an available option, then select the heart.',
    choosePrompt: 'Choose an option, then select the heart to add it to your Secret Santa wishlist.',
    goingToChoose: 'Saved to Saved Gifts. This gift has options - opening it so you can choose one for your Secret Santa wishlist.',
    soldOut: 'Saved to Saved Gifts. This option is sold out, so it wasn’t added to your Secret Santa wishlist.',
    groupFull: 'Saved to Saved Gifts. Your Secret Santa wishlist is full, so this gift wasn’t added.',
    accountFull: 'Your Saved Gifts list is full (200 gifts). Remove something to save more.',
    unknown: 'This gift isn’t available any more.',
    failed: 'Couldn’t save. Try again.',
    removeFailed: 'Couldn’t remove. Try again.',
    ended: 'Secret Santa group saving has ended. Hearts now save to your Saved Gifts.',
    exited: 'You’ve left Secret Santa shopping. Hearts now save to your Saved Gifts.',
    signedOut: 'Sign in to save gifts to your account.',
    importAsk: function (n) { return 'Add ' + n + ' saved ' + (n === 1 ? 'gift' : 'gifts') + ' from this device to your account?'; },
    importYes: 'Add them',
    importNo: 'Not now',
    importDone: function (added, skipped) {
      var s = added === 1 ? 'Added 1 saved gift to your account.' : 'Added ' + added + ' saved gifts to your account.';
      if (skipped > 0) s += ' ' + skipped + (skipped === 1 ? ' couldn’t be added.' : ' couldn’t be added.');
      return s;
    },
    importFailed: 'Couldn’t add the saved gifts from this device. They’re still here - try again later.',
    retry: 'Try again',
    dismiss: 'Dismiss'
  };

  /* ------------------------------------------------------------ storage */

  function ss() { try { return window.sessionStorage; } catch (e) { return null; } }
  function ls() { try { return window.localStorage; } catch (e) { return null; } }
  function sget(key) { try { var s = ss(); return s ? s.getItem(key) : null; } catch (e) { return null; } }
  function sset(key, value) { try { var s = ss(); if (s) s.setItem(key, value); } catch (e) {} }
  function sdel(key) { try { var s = ss(); if (s) s.removeItem(key); } catch (e) {} }
  function lget(key) { try { var l = ls(); return l ? l.getItem(key) : null; } catch (e) { return null; } }
  function lset(key, value) { try { var l = ls(); if (l) l.setItem(key, value); } catch (e) {} }

  /* ------------------------------------------------ 1. the context token */

  /* Fallback capture. layout/theme.liquid already does this synchronously
     before {{ content_for_header }}; this repeats it harmlessly for a page
     where that inline script is missing, and also strips a context that was
     wrongly placed in the query string (never used: it has already been sent
     with the page request). */
  function capture() {
    var loc = window.location;
    var hash = loc.hash ? loc.hash.slice(1) : '';
    var found = false, token = null, rest = [];
    if (hash) {
      var parts = hash.split('&');
      for (var i = 0; i < parts.length; i++) {
        var part = parts[i];
        if (part.indexOf(PARAM + '=') === 0) { found = true; if (token === null) token = part.slice(PARAM.length + 1); }
        else if (part === PARAM) { found = true; }
        else if (part) { rest.push(part); }
      }
    }
    var search = loc.search, stray = false;
    if (search && search.indexOf(PARAM) > -1) {
      try {
        var q = new URLSearchParams(search);
        if (q.has(PARAM)) { stray = true; q['delete'](PARAM); var qs = q.toString(); search = qs ? '?' + qs : ''; }
      } catch (e) {}
    }
    if (!found && !stray) return;
    try {
      window.history.replaceState(window.history.state, '', loc.pathname + search + (rest.length ? '#' + rest.join('&') : ''));
    } catch (e) {}
    if (!found) return;
    try { token = decodeURIComponent(token || ''); } catch (e) { token = ''; }
    if (token && token.length <= 1024 && TOKEN_SHAPE.test(token)) {
      sset(TOKEN_KEY, token);
      sset(TOKEN_FRESH_KEY, '1');
    }
  }

  function storedToken() {
    var t = sget(TOKEN_KEY);
    if (!t || !TOKEN_SHAPE.test(t)) { if (t) sdel(TOKEN_KEY); return null; }
    /* The expiry inside the token is read only to stop using it early; the
       server is the authority on whether it is valid. */
    try {
      var part = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      while (part.length % 4) part += '=';
      var claims = JSON.parse(window.atob(part));
      if (typeof claims.exp !== 'number' || claims.exp * 1000 <= Date.now()) { sdel(TOKEN_KEY); return null; }
    } catch (e) { sdel(TOKEN_KEY); return null; }
    return t;
  }

  function clearContext() {
    sdel(TOKEN_KEY);
    sdel(TOKEN_FRESH_KEY);
    sdel(PROMPT_KEY);
    ctx = null;
    removeBar();
  }

  /* ------------------------------------------------------------ 2. state */

  /* Account Saved Gifts: handles, in server order. */
  var accountList = [];
  var accountSet = {};
  /* Secret Santa context, or null. items: handle -> variant id (string) or null. */
  var ctx = null;
  var hydrated = false;
  var lastSync = 0;
  var listing = null;
  var inflight = {};
  var failure = null;
  var signedOutOnServer = false;

  function setAccount(handles) {
    accountList = [];
    accountSet = {};
    for (var i = 0; i < handles.length; i++) {
      var h = String(handles[i]);
      if (!accountSet[h]) { accountSet[h] = true; accountList.push(h); }
    }
  }
  function accountAdd(h) { if (!accountSet[h]) { accountSet[h] = true; accountList.push(h); } }
  function accountRemove(h) {
    if (!accountSet[h]) return;
    delete accountSet[h];
    for (var i = 0; i < accountList.length; i++) if (accountList[i] === h) { accountList.splice(i, 1); break; }
  }

  function repaint() {
    var w = window.CadooWishlist;
    if (w && typeof w.sync === 'function') { try { w.sync(); } catch (e) {} }
  }

  /* ---------------------------------------------------------- 3. server */

  function post(body) {
    return window.fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Cadoo-Wishlist': '1'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(
        function (json) { return { status: res.status, body: json }; },
        function () { return { status: res.status, body: null }; }
      );
    });
  }

  function reasonOf(r) { return r && r.body && r.body.ok === false ? r.body.reason : null; }

  /* The one list request per page: replaces in-memory state wholesale. */
  function hydrate() {
    if (listing) return listing;
    var token = storedToken();
    var body = { intent: 'list' };
    if (token) body.token = token;
    var hadToken = !!token;
    listing = post(body).then(function (r) {
      listing = null;
      if (r.body && r.body.ok === true && r.body.state === 'list') {
        signedOutOnServer = false;
        setAccount(r.body.account && Array.isArray(r.body.account.handles) ? r.body.account.handles : []);
        var c = r.body.context;
        if (c && c.active === true) {
          ctx = {
            token: token,
            groupName: String((c.group && c.group.name) || ''),
            items: sanitizeItems(c.items)
          };
          renderBar();
          sdel(TOKEN_FRESH_KEY);
        } else {
          var hadCtx = !!ctx;
          if (hadToken && c && c.reason === 'invalid-context') {
            var wasFresh = sget(TOKEN_FRESH_KEY) === '1';
            clearContext();
            if (hadCtx || wasFresh) say(TEXT.ended);
          } else if (!hadToken) {
            clearContext();
          }
        }
        hydrated = true;
        lastSync = Date.now();
        repaint();
        maybeOfferImport();
        maybePromptForOption();
        return true;
      }
      if (reasonOf(r) === 'signed-out') {
        /* The Liquid said signed in; the storefront session has since ended.
           Show nothing as saved rather than another customer's list. */
        signedOutOnServer = true;
        setAccount([]);
        clearContext();
        hydrated = true;
        lastSync = Date.now();
        repaint();
        return false;
      }
      /* Unreachable or an error: keep whatever is painted (cache or empty). */
      return false;
    }, function () {
      listing = null;
      return false;
    });
    return listing;
  }

  function sanitizeItems(items) {
    var out = {};
    if (!items || typeof items !== 'object') return out;
    for (var k in items) {
      if (!Object.prototype.hasOwnProperty.call(items, k)) continue;
      var v = items[k];
      out[k] = typeof v === 'string' && /^\d{1,20}$/.test(v) ? v : null;
    }
    return out;
  }

  /* ---------------------------------------------------- 4. where is it? */

  function isSavedSurface(el) {
    return !!(el.closest && el.closest('[data-cd-saved-grid], [data-cd-saved-body]'));
  }

  /* The product page's own heart and the option its picker has chosen, read
     from the page's existing state (the cart form's hidden variant input,
     kept current by cadoo.js, and the variant data the page prints). Null
     when the heart is not the page's own product (a related-product card). */
  function productPage(heart, handle) {
    var root = heart.closest && heart.closest('[data-cd-pdp]');
    if (!root || heart.closest('.cd-card')) return null;
    var input = root.querySelector('[data-cd-variant-id]');
    var dataEl = root.querySelector('[data-cd-pdp-data]');
    if (!input || !dataEl) return null;
    var url = (root.getAttribute('data-cd-url') || '').split(/[?#]/)[0];
    var own = '';
    try { own = decodeURIComponent(url.split('/').pop() || ''); } catch (e) { own = ''; }
    if (own !== handle) return null;
    return {
      selected: function () {
        var id = String(input.value || '').trim();
        if (!/^\d{1,20}$/.test(id)) return null;
        var variants = [];
        try { variants = JSON.parse(dataEl.textContent || '{}').variants || []; } catch (e) { variants = []; }
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === id) return { id: id, available: variants[i].available === true };
        }
        return null;
      }
    };
  }

  function rootUrl() {
    var root = (CFG.routes && CFG.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function productPageUrl(heart, handle) {
    var card = heart.closest && heart.closest('.cd-card');
    var link = card && card.querySelector('a.cd-card__link[href], .cd-card__title a[href]');
    if (link && link.getAttribute('href')) return link.getAttribute('href').split('#')[0];
    return rootUrl() + 'products/' + encodeURIComponent(handle);
  }

  /* ----------------------------------------------------------- 5. the UI */

  var bar = null, live = null, toastEl = null, toastTimer = null, importEl = null;

  function injectStyle() {
    if (document.getElementById('cd-ss-style')) return;
    var css =
      '.cd-ss-bar{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:4px 12px;' +
      'margin:0;padding:8px 16px;background:#fef6f2;border-bottom:1px solid rgba(244,124,92,.35);' +
      'color:var(--cd-navy,#14213d);font-size:14px;line-height:1.4;text-align:center}' +
      '.cd-ss-bar__text{margin:0;min-width:0;overflow-wrap:anywhere}' +
      '.cd-ss-bar__text strong{font-weight:700}' +
      '.cd-ss-btn{appearance:none;background:transparent;border:1px solid currentColor;border-radius:999px;' +
      'color:inherit;font:inherit;font-weight:700;font-size:13px;line-height:1;min-height:32px;padding:0 14px;cursor:pointer}' +
      '.cd-ss-btn:hover{background:rgba(20,33,61,.06)}' +
      '.cd-ss-btn:focus-visible{outline:2px solid var(--cd-navy,#14213d);outline-offset:2px}' +
      '.cd-ss-toast{position:fixed;left:16px;right:16px;bottom:16px;z-index:95;margin:0 auto;max-width:28rem;' +
      'display:flex;flex-wrap:wrap;align-items:center;gap:8px 12px;padding:12px 16px;border-radius:14px;' +
      'background:var(--cd-navy,#14213d);color:#faf7f2;font-size:14px;line-height:1.4;' +
      'box-shadow:0 12px 32px -12px rgba(20,33,61,.45)}' +
      '.cd-ss-toast[hidden]{display:none}' +
      '.cd-ss-toast__text{flex:1 1 12rem;margin:0}' +
      '.cd-ss-toast .cd-ss-btn{color:#faf7f2}' +
      '.cd-ss-toast .cd-ss-btn:focus-visible{outline-color:#faf7f2}' +
      '.cd-ss-import{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:8px 12px;' +
      'margin:0;padding:10px 16px;background:#f3f6f4;border-bottom:1px solid rgba(20,33,61,.12);' +
      'color:var(--cd-navy,#14213d);font-size:14px;line-height:1.4;text-align:center}' +
      '.cd-ss-import__text{margin:0;min-width:0;overflow-wrap:anywhere}' +
      '.cd-ss-sr{position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}' +
      '[data-cd-wish][aria-busy="true"]{cursor:progress;opacity:.6}' +
      '@media (prefers-reduced-motion:no-preference){.cd-ss-toast{transition:opacity .2s ease}}';
    var style = document.createElement('style');
    style.id = 'cd-ss-style';
    style.appendChild(document.createTextNode(css));
    (document.head || document.documentElement).appendChild(style);
  }

  function ensureLive() {
    if (live && document.contains(live)) return live;
    live = document.createElement('div');
    live.className = 'cd-ss-sr';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('aria-atomic', 'true');
    live.setAttribute('data-cd-ss-live', '');
    document.body.appendChild(live);
    return live;
  }

  function mainEl() { return document.getElementById('cd-main') || document.querySelector('main'); }

  function renderBar() {
    if (!ctx) return;
    injectStyle();
    ensureLive();
    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'cd-ss-bar';
      bar.setAttribute('role', 'region');
      bar.setAttribute('aria-label', TEXT.region);
      bar.setAttribute('data-cd-ss-bar', '');
      var text = document.createElement('p');
      text.className = 'cd-ss-bar__text';
      text.appendChild(document.createTextNode(TEXT.indicator));
      var name = document.createElement('strong');
      name.setAttribute('data-cd-ss-group', '');
      text.appendChild(name);
      var exit = document.createElement('button');
      exit.type = 'button';
      exit.className = 'cd-ss-btn';
      exit.setAttribute('data-cd-ss-exit', '');
      exit.setAttribute('aria-label', TEXT.exitLabel);
      exit.textContent = TEXT.exit;
      exit.addEventListener('click', onExit);
      bar.appendChild(text);
      bar.appendChild(exit);
      var main = mainEl();
      if (main) main.insertBefore(bar, main.firstChild);
      else document.body.insertBefore(bar, document.body.firstChild);
    }
    // textContent only: a group name is never parsed as markup.
    bar.querySelector('[data-cd-ss-group]').textContent = ctx.groupName;
  }

  function removeBar() {
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
    bar = null;
  }

  function toastOffset() {
    var sticky = document.querySelector('.cd-p-sticky.is-on');
    var extra = 0;
    if (sticky && sticky.getBoundingClientRect) extra = sticky.getBoundingClientRect().height || 0;
    return (16 + extra) + 'px';
  }

  /* Visible toast + polite live region. A failure stays until retried or
     dismissed; anything else fades after a few seconds. */
  function say(message, retry) {
    injectStyle();
    ensureLive();
    if (!toastEl || !document.contains(toastEl)) {
      toastEl = document.createElement('div');
      toastEl.className = 'cd-ss-toast';
      toastEl.setAttribute('data-cd-ss-toast', '');
      toastEl.hidden = true;
      document.body.appendChild(toastEl);
    }
    clearTimeout(toastTimer);
    toastEl.innerHTML = '';
    var p = document.createElement('p');
    p.className = 'cd-ss-toast__text';
    p.textContent = message;
    toastEl.appendChild(p);
    if (retry) {
      var again = document.createElement('button');
      again.type = 'button';
      again.className = 'cd-ss-btn';
      again.setAttribute('data-cd-ss-retry', '');
      again.textContent = TEXT.retry;
      again.addEventListener('click', onRetry);
      toastEl.appendChild(again);
    }
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'cd-ss-btn';
    close.setAttribute('data-cd-ss-dismiss', '');
    close.textContent = TEXT.dismiss;
    close.addEventListener('click', function () {
      var f = failure;
      hideToast();
      if (retry) { failure = null; giveFocusBack(f); }
    });
    toastEl.appendChild(close);
    toastEl.style.bottom = toastOffset();
    toastEl.hidden = false;
    live.textContent = '';
    window.setTimeout(function () { if (live) live.textContent = message; }, 30);
    if (!retry) toastTimer = window.setTimeout(hideToast, 6000);
  }

  /* Announced only: for successes that the heart itself already shows. */
  function announce(message) {
    ensureLive();
    live.textContent = '';
    window.setTimeout(function () { if (live) live.textContent = message; }, 30);
  }

  function hideToast() {
    clearTimeout(toastTimer);
    if (toastEl) toastEl.hidden = true;
  }

  function setBusy(handle, on) {
    if (on) inflight[handle] = true; else delete inflight[handle];
    var hearts = document.querySelectorAll('[data-cd-wish]');
    for (var i = 0; i < hearts.length; i++) {
      if (hearts[i].getAttribute('data-cd-wish') !== handle) continue;
      if (on) hearts[i].setAttribute('aria-busy', 'true');
      else hearts[i].removeAttribute('aria-busy');
    }
  }

  function giveFocusBack(f) {
    if (f && f.heart && document.contains(f.heart)) { try { f.heart.focus(); } catch (e) {} }
  }

  /* ------------------------------------------------------- 6. the provider */

  function heartState(button, list) {
    var handle = button.getAttribute('data-cd-wish');
    if (!handle) return false;
    if (isSavedSurface(button)) return list.indexOf(handle) > -1;
    if (!ctx) return list.indexOf(handle) > -1;
    if (!Object.prototype.hasOwnProperty.call(ctx.items, handle)) return false;
    var page = productPage(button, handle);
    if (!page) return true;
    var saved = ctx.items[handle];
    if (saved === null) return true;
    var v = page.selected();
    return !!(v && v.id === saved);
  }

  function heartLabel(button, on, title) {
    if (!ctx || isSavedSurface(button)) return null;
    return 'Save ' + title + ' to Secret Santa wishlist';
  }

  var provider = {
    get: function () { return accountList.slice(); },
    toggle: function (handle) {
      /* Compatibility for CadooWishlist.toggle(handle) callers: account semantics. */
      if (typeof handle !== 'string' || !handle) return false;
      accountToggle(handle, null, false);
      return !!accountSet[handle];
    },
    state: heartState,
    label: heartLabel
  };

  /* ------------------------------------------------------- 7. actions */

  function onClick(e) {
    var heart = e.target && e.target.closest && e.target.closest('[data-cd-wish]');
    if (!heart) return;
    var handle = heart.getAttribute('data-cd-wish');
    if (!handle) return;
    /* From here on this click is ours: the theme's bubbling handler, which
       would toggle the anonymous list, must not run. */
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    if (inflight[handle]) return;
    var keyboard = e.detail === 0;
    if (signedOutOnServer) { say(TEXT.signedOut); return; }
    if (isSavedSurface(heart) || !ctx) { accountToggle(handle, heart, keyboard); return; }
    groupToggle(handle, heart, keyboard);
  }

  function accountToggle(handle, heart, keyboard) {
    if (accountSet[handle]) {
      run({ kind: 'account-remove', handle: handle, heart: heart, keyboard: keyboard });
      return;
    }
    var variantId = null;
    var page = heart ? productPage(heart, handle) : null;
    if (page) { var v = page.selected(); if (v) variantId = v.id; }
    run({ kind: 'account-save', handle: handle, variantId: variantId, heart: heart, keyboard: keyboard });
  }

  function groupToggle(handle, heart, keyboard) {
    var inGroup = Object.prototype.hasOwnProperty.call(ctx.items, handle);
    var page = productPage(heart, handle);
    if (page) {
      var v = page.selected();
      if (!v || !v.available) { say(TEXT.chooseOption); return; }
      var exact = inGroup && (ctx.items[handle] === null || ctx.items[handle] === v.id);
      if (exact) { run({ kind: 'group-remove', handle: handle, heart: heart, keyboard: keyboard }); return; }
      run({ kind: 'group-save', handle: handle, variantId: v.id, heart: heart, keyboard: keyboard });
      return;
    }
    if (inGroup) { run({ kind: 'group-remove', handle: handle, heart: heart, keyboard: keyboard }); return; }
    /* A card: the server resolves a single-option product's exact variant
       itself and tells us when the customer has to choose. */
    run({ kind: 'group-save', handle: handle, variantId: null, heart: heart, keyboard: keyboard });
  }

  function run(action) {
    var handle = action.handle;
    setBusy(handle, true);
    failure = null;
    hideToast();
    var body;
    if (action.kind === 'account-save') {
      body = { intent: 'save-gift', handle: handle };
      if (action.variantId) body.variantId = action.variantId;
    } else if (action.kind === 'account-remove') {
      body = { intent: 'remove-gift', handle: handle, scope: 'account' };
    } else if (action.kind === 'group-save') {
      if (!ctx) { setBusy(handle, false); accountToggle(handle, action.heart, action.keyboard); return; }
      body = { intent: 'save-gift', handle: handle, token: ctx.token };
      if (action.variantId) body.variantId = action.variantId;
    } else {
      if (!ctx) { setBusy(handle, false); return; }
      body = { intent: 'remove-gift', handle: handle, scope: 'group', token: ctx.token };
    }
    post(body).then(function (r) {
      setBusy(handle, false);
      if (r.body && r.body.ok === true) { applySuccess(action, r.body); return; }
      var reason = reasonOf(r);
      if (reason === 'signed-out') { signedOutOnServer = true; setAccount([]); clearContext(); repaint(); say(TEXT.signedOut); return; }
      if (reason === 'invalid-context') { clearContext(); repaint(); say(TEXT.ended); return; }
      if (reason === 'limit') { say(TEXT.accountFull); return; }
      if (reason === 'unknown-product') { say(TEXT.unknown); return; }
      fail(action);
    }, function () {
      setBusy(handle, false);
      fail(action);
    });
  }

  function applySuccess(action, body) {
    var handle = action.handle;
    if (body.state === 'saved-gift') {
      accountAdd(handle);
      if (body.group === 'added' || body.group === 'replaced' || body.group === 'exists') {
        if (ctx) ctx.items[handle] = typeof body.variantId === 'string' ? body.variantId : null;
      }
      repaint();
      if (action.kind === 'account-save') { announce(TEXT.saved); return; }
      if (body.group === 'added') { say(TEXT.groupAdded); return; }
      if (body.group === 'replaced') { say(TEXT.groupReplaced); return; }
      if (body.group === 'exists') { announce(TEXT.groupAdded); return; }
      if (body.group === 'skipped') {
        if (body.groupSkipped === 'choose') {
          /* Several options: never pick one for them. Saved to the account;
             open the product in this tab (the context goes with it) and ask there. */
          if (action.heart && !productPage(action.heart, handle)) {
            sset(PROMPT_KEY, handle);
            say(TEXT.goingToChoose);
            window.location.assign(productPageUrl(action.heart, handle));
          } else {
            say(TEXT.chooseOption);
          }
          return;
        }
        if (body.groupSkipped === 'unavailable') { say(TEXT.soldOut); return; }
        if (body.groupSkipped === 'limit') { say(TEXT.groupFull); return; }
        say(TEXT.saved);
        return;
      }
      announce(TEXT.saved);
      return;
    }
    if (body.state === 'removed-gift') {
      if (body.scope === 'group') {
        if (ctx) delete ctx.items[handle];
        repaint();
        say(TEXT.groupRemoved);
        return;
      }
      accountRemove(handle);
      repaint();
      announce(TEXT.unsaved);
    }
  }

  function fail(action) {
    failure = action;
    say(action.kind === 'account-remove' || action.kind === 'group-remove' ? TEXT.removeFailed : TEXT.failed, true);
    if (action.keyboard && toastEl) {
      var again = toastEl.querySelector('[data-cd-ss-retry]');
      if (again) { try { again.focus(); } catch (e) {} }
    }
  }

  function onRetry() {
    var f = failure;
    failure = null;
    hideToast();
    if (!f) return;
    if (inflight[f.handle]) return;
    giveFocusBack(f);
    run(f);
  }

  function onExit() {
    clearContext();
    repaint();
    say(TEXT.exited);
    var main = mainEl();
    if (main && main.focus) { try { main.focus(); } catch (e) {} }
  }

  /* ------------------------------------------------ 8. import prompt */

  function anonymousList() {
    try {
      var l = window.CadooWishlist && window.CadooWishlist.local ? window.CadooWishlist.local.get() : JSON.parse(lget(ANON_KEY) || '[]');
      return Array.isArray(l) ? l.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) { return []; }
  }

  function maybeOfferImport() {
    if (importEl || signedOutOnServer) return;
    if (lget(IMPORT_FLAG_KEY) === 'declined') return;
    var list = anonymousList();
    if (!list.length) return;
    injectStyle();
    ensureLive();
    importEl = document.createElement('div');
    importEl.className = 'cd-ss-import';
    importEl.setAttribute('role', 'region');
    importEl.setAttribute('aria-label', 'Saved gifts on this device');
    importEl.setAttribute('data-cd-ss-import', '');
    var p = document.createElement('p');
    p.className = 'cd-ss-import__text';
    p.textContent = TEXT.importAsk(list.length);
    var yes = document.createElement('button');
    yes.type = 'button';
    yes.className = 'cd-ss-btn';
    yes.setAttribute('data-cd-ss-import-yes', '');
    yes.textContent = TEXT.importYes;
    var no = document.createElement('button');
    no.type = 'button';
    no.className = 'cd-ss-btn';
    no.setAttribute('data-cd-ss-import-no', '');
    no.textContent = TEXT.importNo;
    yes.addEventListener('click', onImport);
    no.addEventListener('click', function () {
      lset(IMPORT_FLAG_KEY, 'declined');
      removeImport();
    });
    importEl.appendChild(p);
    importEl.appendChild(yes);
    importEl.appendChild(no);
    var main = mainEl();
    var anchor = bar && bar.parentNode === main ? bar.nextSibling : (main ? main.firstChild : null);
    if (main) main.insertBefore(importEl, anchor);
    else document.body.insertBefore(importEl, document.body.firstChild);
  }

  function removeImport() {
    if (importEl && importEl.parentNode) importEl.parentNode.removeChild(importEl);
    importEl = null;
  }

  var importing = false;
  function onImport() {
    if (importing) return;
    var list = anonymousList();
    if (!list.length) { removeImport(); return; }
    importing = true;
    var yes = importEl && importEl.querySelector('[data-cd-ss-import-yes]');
    if (yes) yes.disabled = true;
    var chunks = [];
    for (var i = 0; i < list.length; i += IMPORT_CHUNK) chunks.push(list.slice(i, i + IMPORT_CHUNK));
    var added = 0, skipped = 0, ok = true;
    var chain = Promise.resolve();
    chunks.forEach(function (chunk) {
      chain = chain.then(function () {
        if (!ok) return;
        return post({ intent: 'import-gifts', handles: chunk }).then(function (r) {
          if (r.body && r.body.ok === true && r.body.state === 'imported') {
            added += r.body.imported || 0;
            skipped += (r.body.skippedLimit || 0) + (r.body.ignored || 0);
          } else { ok = false; }
        }, function () { ok = false; });
      });
    });
    chain.then(function () {
      importing = false;
      if (!ok) {
        if (yes) yes.disabled = false;
        say(TEXT.importFailed);
        return;
      }
      /* Only after the server confirmed: the device list is now the account's. */
      try { if (window.CadooWishlist && window.CadooWishlist.local) window.CadooWishlist.local.set([]); else lset(ANON_KEY, '[]'); } catch (e) {}
      removeImport();
      say(TEXT.importDone(added, skipped));
      hydrate();
    });
  }

  /* ----------------------------------------------- 9. product page prompt */

  function maybePromptForOption() {
    if (!ctx) { sdel(PROMPT_KEY); return; }
    var pending = sget(PROMPT_KEY);
    if (!pending) return;
    var root = document.querySelector('[data-cd-pdp]');
    if (!root) return;
    var url = (root.getAttribute('data-cd-url') || '').split(/[?#]/)[0];
    var own = '';
    try { own = decodeURIComponent(url.split('/').pop() || ''); } catch (e) { own = ''; }
    if (own !== pending) return;
    sdel(PROMPT_KEY);
    say(TEXT.choosePrompt);
  }

  /* --------------------------------------------------------- 10. start */

  function install() {
    var w = window.CadooWishlist;
    if (!w || typeof w.use !== 'function') return false;
    /* Neutral first paint: no account handles, no group, no bar, until the
       signed list response for THIS customer arrives. Nothing authenticated
       is ever read from storage, so nothing of a previous customer can show. */
    w.use(provider);
    document.addEventListener('click', onClick, true);
    document.addEventListener('cadoo:variant', function () { repaint(); });
    window.addEventListener('popstate', function () { repaint(); });
    window.addEventListener('pageshow', function (e) {
      if (e && e.persisted) hydrate();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastSync > REVALIDATE_AFTER_MS) hydrate();
    });
    return true;
  }

  function start() {
    hydrate();
  }

  /* Public surface: booleans only. No customer-specific state (handles,
     group name, items) is reachable from here; the hearts' aria-pressed and
     the engine's own CadooWishlist.get() are the only views of it, exactly
     as for the device list. */
  window.CadooSecretSanta = {
    version: 3,
    isActive: function () { return !!ctx; },
    isHydrated: function () { return hydrated; }
  };

  capture();
  /* An earlier build persisted authenticated state under this key. Remove
     it on every start, signed in or out, so nothing from it can ever be
     shown or read again. */
  sdel(LEGACY_CACHE_KEY);
  if (!SIGNED_IN) {
    /* Anonymous: the theme's own engine owns the heart. */
    return;
  }
  if (!install()) return;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
